"use client";

import { useCallback, useEffect, useState } from "react";
import type { BaseSyntheticEvent } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { applyRequiredFieldErrors } from "@/utils/forms/requiredFieldHelpers";
import { submitQuoteRequestAjax } from "@/utils/actions/requests";
import { createClient as createSupabaseBrowserClient } from "@/utils/supabase/client";
import { PROPERTY_TYPE_OPTIONS, SERVICE_OPTIONS } from "@/utils/constants/forms";

const PHONE_MIN_LENGTH = 5;

export const quoteFormSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(1, "Nome obbligatorio"),
  lastName: z
    .string()
    .trim()
    .min(1, "Cognome obbligatorio"),
  email: z
    .string()
    .trim()
    .email("Email non valida"),
  phone: z
    .string()
    .trim()
    .min(
      PHONE_MIN_LENGTH,
      "Telefono obbligatorio"
    ),
  address: z
    .string()
    .trim()
    .optional()
    .nullable()
    .transform((v) => v || ""),
  city: z
    .string()
    .trim()
    .optional()
    .nullable()
    .transform((v) => v || ""),
  propertyType: z
    .string()
    .trim()
    .optional()
    .nullable()
    .transform((v) => v || ""),
  serviceId: z
    .string()
    .trim()
    .optional()
    .nullable()
    .transform((v) => v || ""),
  serviceName: z
    .string()
    .trim()
    .optional()
    .nullable()
    .transform((v) => v || ""),
  problemDescription: z
    .string()
    .trim()
    .optional()
    .nullable()
    .transform((v) => v || ""),
  redirectTo: z
    .string()
    .trim()
    .optional()
    .nullable()
    .transform((v) => v || ""),
  privacyAccepted: z
    .boolean()
    .refine((value) => value, {
      message: "Devi accettare la privacy",
    }),
});

export type QuoteFormValues = z.infer<typeof quoteFormSchema>;

export type QuoteSelectOption = { value: string; label: string };

interface UseQuoteRequestFormOptions {
  defaultService?: string;
  serviceOptions?: QuoteSelectOption[];
  onSubmitSuccess?: () => void;
}

interface UseQuoteRequestFormResult {
  form: ReturnType<typeof useForm<QuoteFormValues>>;
  options: QuoteSelectOption[];
  propertyTypeOptions: typeof PROPERTY_TYPE_OPTIONS;
  isSubmitting: boolean;
  isLoadingOptions: boolean;
  handleSubmit: (event?: BaseSyntheticEvent) => void;
  handleServiceChange: (value: string) => void;
  resetForm: () => void;
}

export function useQuoteRequestForm({
  defaultService,
  serviceOptions,
  onSubmitSuccess,
}: UseQuoteRequestFormOptions): UseQuoteRequestFormResult {
  const [options, setOptions] = useState<QuoteSelectOption[]>(
    serviceOptions && serviceOptions.length > 0 ? serviceOptions : SERVICE_OPTIONS
  );
  const [isLoadingOptions, setIsLoadingOptions] = useState(
    !serviceOptions || serviceOptions.length === 0
  );
  const [hasUserSelectedService, setHasUserSelectedService] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<QuoteFormValues>({
    resolver: zodResolver(quoteFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      propertyType: "",
      serviceId: "",
      serviceName: "",
      problemDescription: "",
      redirectTo: "",
      privacyAccepted: false,
    },
    mode: "onSubmit",
  });

  useEffect(() => {
    let active = true;
    if (serviceOptions && serviceOptions.length > 0) {
      return;
    }

    async function loadServices() {
      try {
        const supabase = createSupabaseBrowserClient();
        const { data, error } = await supabase
          .from("services")
          .select("*")
          .neq("is_deleted", true)
          .order("name", { ascending: true });

        if (!active) return;

        if (error || !data) {
          setOptions(SERVICE_OPTIONS);
          return;
        }

        const normalized = data.map((service) => ({
          value: String(service.id),
          label: service.name,
        }));
        setOptions(normalized);
      } catch (error) {
        if (active) {
          setOptions(SERVICE_OPTIONS);
        }
      } finally {
        if (active) {
          setIsLoadingOptions(false);
        }
      }
    }

    loadServices();

    return () => {
      active = false;
    };
  }, [serviceOptions]);

  useEffect(() => {
    form.setValue("redirectTo", window.location.pathname, {
      shouldDirty: false,
      shouldTouch: false,
    });
  }, [form]);

  const resolveServiceSelection = useCallback(
    (value?: string | null) => {
      if (!value) {
        return { id: "", label: "" };
      }

      const normalized = value.toLowerCase();
      const byValue = options.find((opt) => opt.value === value);
      if (byValue) {
        return { id: byValue.value, label: byValue.label };
      }

      const byLabel = options.find(
        (opt) => opt.label.toLowerCase() === normalized
      );
      if (byLabel) {
        return { id: byLabel.value, label: byLabel.label };
      }

      return { id: value, label: value };
    },
    [options]
  );

  useEffect(() => {
    if (hasUserSelectedService) return;
    const selection = resolveServiceSelection(defaultService);
    form.setValue("serviceId", selection.id, {
      shouldDirty: false,
      shouldTouch: false,
    });
    form.setValue("serviceName", selection.label, {
      shouldDirty: false,
      shouldTouch: false,
    });
  }, [defaultService, form, hasUserSelectedService, resolveServiceSelection]);

  const createFormDataFromValues = useCallback(
    (values: QuoteFormValues): FormData => {
      const fd = new FormData();
      fd.append("firstName", values.firstName);
      fd.append("lastName", values.lastName);
      fd.append("email", values.email);
      fd.append("phone", values.phone);
      if (values.address) fd.append("address", values.address);
      if (values.city) fd.append("city", values.city);
      if (values.propertyType) fd.append("propertyType", values.propertyType);
      if (values.serviceId) fd.append("serviceId", values.serviceId);
      const selection = resolveServiceSelection(values.serviceId || values.serviceName);
      if (selection.label) {
        fd.append("serviceName", selection.label);
      }
      if (values.problemDescription) {
        fd.append("problemDescription", values.problemDescription);
      }
      fd.append("privacyAccepted", values.privacyAccepted ? "on" : "");
      if (values.redirectTo) {
        fd.append("redirectTo", values.redirectTo);
      }
      return fd;
    },
    [resolveServiceSelection]
  );

  const resetToDefaults = useCallback(() => {
    const selection = resolveServiceSelection(defaultService);
    form.reset({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      propertyType: "",
      serviceId: selection.id,
      serviceName: selection.label,
      problemDescription: "",
      redirectTo: window.location.pathname,
      privacyAccepted: false,
    });
    setHasUserSelectedService(false);
  }, [defaultService, form, resolveServiceSelection]);

  const onValid = useCallback(
    async (values: QuoteFormValues) => {
      setIsSubmitting(true);
      try {
        const formData = createFormDataFromValues(values);
        const result = await submitQuoteRequestAjax(formData);
        if (result.success) {
          toast.success("Richiesta inviata", {
            description: "La tua richiesta di servizio è stata inviata con successo",
          });
          resetToDefaults();
          onSubmitSuccess?.();
        } else {
          const message =
            result.error || "Si è verificato un errore inatteso";
          toast.error("Richiesta non inviata", {
            description: message,
          });
        }
      } catch (error) {
        toast.error("Richiesta non inviata", {
          description:
            error instanceof Error
              ? error.message
              : "Impossibile inviare la richiesta. Riprova.",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [createFormDataFromValues, onSubmitSuccess, resetToDefaults]
  );

  const handleSubmit = (e?: BaseSyntheticEvent) => {
    if (e?.preventDefault) e.preventDefault();
    const vals = form.getValues() as QuoteFormValues;
    const missingCount = applyRequiredFieldErrors(form, vals, [
      { key: 'firstName', message: 'Nome obbligatorio', isMissing: !vals.firstName },
      { key: 'lastName', message: 'Cognome obbligatorio', isMissing: !vals.lastName },
      { key: 'email', message: 'Email non valida', isMissing: !vals.email },
      { key: 'phone', message: 'Telefono obbligatorio', isMissing: !vals.phone },
      { key: 'privacyAccepted', message: 'Devi accettare la privacy', isMissing: !vals.privacyAccepted },
    ]);
    if (missingCount > 0) return; // rely on inline errors only
    form.handleSubmit(onValid)(e as any);
  };

  const handleServiceChange = useCallback(
    (value: string) => {
      setHasUserSelectedService(true);
      const selection = resolveServiceSelection(value);
      form.setValue("serviceId", selection.id, { shouldDirty: true });
      form.setValue("serviceName", selection.label, { shouldDirty: true });
    },
    [form, resolveServiceSelection]
  );

  return {
    form,
    options,
    propertyTypeOptions: PROPERTY_TYPE_OPTIONS,
    isSubmitting,
    isLoadingOptions,
    handleSubmit,
    handleServiceChange,
    resetForm: resetToDefaults,
  };
}
